import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import ConicalIcon from './components/ConicalIcon';
import SwabIcon from './components/SwabIcon';
import TubeIcon from './components/TubeIcon'; // Added import

export interface TestTube {
  toolType?: 'TUBE' | 'SWAB' | 'CONICAL'; // Added optional field
  mainTitle: string;
  shortName: string;
  c: string;
  performingLaboratory: string;
  sampleSource: string;
  collectionTool: string;
  color: string;
  collectionInstructions: string;
  clarificationsAndExplanations: string;
  timeToResults: string;
  testCode: string;
  notes: string;
  keywords: string[];
  hiddenKeywords: string[]; // Add hiddenKeywords property
}

export interface TestTubes {
  tubes: TestTube[];
  substitutesTubes: string;
}

interface TestTubeRowProps {
  tube: TestTube;
  index: number;
  expandedId: number | null;
  toggleExpand: (index: number) => void;
}

// Helper function to check if a string is a URL
const isUrl = (str: string): boolean => {
  try {
    new URL(str);
    return true;
  } catch {
    return str.startsWith('http://') || str.startsWith('https://');
  }
};

const TestTubeRow: React.FC<TestTubeRowProps> = ({ 
  tube, 
  index, 
  expandedId, 
  toggleExpand 
}) => {
  const isExpanded = expandedId === index;
  
  // Get first tube color only
  const getTubeColor = () => {
    if (!tube.color) return '';
    switch (tube.color) {
      case 'red':
        return '#f7020f';
      case 'yellow':
        return '#f1f50c';  
      case 'blue':
        return '#103C6E';
      case 'light blue':
        return '#34aeeb';
      case 'orange':
        return '#ff7700';
      case 'white':
        return '#ffffff';
      case 'purple':
        return '#A349A4';
      case 'pink':
        return '#f5cbd5';
      case 'green':
          return '#01770D';
      case 'gray':
        return '#707070';  
      default:
        return '#ffffff'      
    }
  };
    
  return (
    <div style={{
      marginBottom: '12px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 2px 4px #0000001a'
    }}>
      {/* Main row container */}
      <div 
        onClick={() => toggleExpand(index)}
        style={{ 
          cursor: 'pointer',
          backgroundColor: 'white',
          transition: 'background-color 0.2s ease',
          padding: '12px 16px',
          position: 'relative',
          minHeight: '60px', // Ensure enough height for the content
          display: 'grid',
          gridTemplateColumns: '40px 1fr 40px' // Left: icons, Middle: content, Right: arrows
        }}
      >
        {/* Left column: SVG tube icon in a circle, vertically centered */}
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          alignSelf: 'center', // Vertical center in grid cell
          gridColumn: '1'
        }}>
          <div style={{
            width: '36px',
            height: '36px',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* Conditionally render the correct icon */}
            {tube.toolType === 'CONICAL' && <ConicalIcon color={"#FFFFFF"} />}
            {tube.toolType === 'SWAB' && <SwabIcon color={"#FFFFFF"} />}
            {(tube.toolType === 'TUBE' || !tube.toolType) && <TubeIcon color={getTubeColor()} />}
          </div>
        </div>

        {/* Middle column: Title and Collection Tool */}
        <div style={{
            display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gridColumn: '2',
          marginRight: '10px'
        }}>
          {/* Title */}
          <div style={{ 
            textAlign: 'right',
            fontWeight: 'bold'
          }}>
            {tube.mainTitle || tube.shortName}
          </div>
          
          {/* Collection Tool (replacing collectionInstructions) */}
          {tube.collectionTool && (
            <div style={{ 
              textAlign: 'right',
              fontSize: '14px',
              color: '#4b5563'
            }}>
              {tube.collectionTool}
            </div>
          )}
        </div>

        {/* Right column: Chevron, vertically centered */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          alignSelf: 'center', // Vertical center in grid cell
          gridColumn: '3'
        }}>
          {isExpanded ? (
            <ChevronUp style={{ 
              width: '20px', 
              height: '20px',
              transition: 'transform 0.3s ease'
            }} />
          ) : (
            <ChevronDown style={{ 
              width: '20px', 
              height: '20px',
              transition: 'transform 0.3s ease'
            }} />
          )}
        </div>
      </div>

      {/* Expandable content with CSS transition */}
      <div
        style={{ 
          maxHeight: isExpanded ? '1000px' : '0',
          opacity: isExpanded ? '1' : '0',
          overflow: 'hidden',
          transition: 'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out'
        }}
      >
        <div style={{
          padding: '16px',
          textAlign: 'right',
          borderTop: '1px solid #e5e7eb'
        }}>          
          {/* Other fields (removed testCode, timeToResults, and clarificationsAndExplanations) */}
          {[
            { label: "מעבדה מבצעת", value: tube.performingLaboratory },
            { label: "מקור דגימה", value: tube.sampleSource },
            { label: "כלי איסוף", value: tube.collectionTool },
            { label: "הנחיות לקיחה", value: tube.collectionInstructions ? (
              isUrl(tube.collectionInstructions) ? 
                <><a href={tube.collectionInstructions} target="_blank" rel="noopener noreferrer">לינק</a> <span>(ניתן לפתיחה מרשת של בית החולים בלבד)</span></> : 
                tube.collectionInstructions
            ) : "" },
            { label: "הערות", value: tube.notes }
          ].map((field, idx) => (
            field.value ? (
              <div key={idx} style={{ marginBottom: '12px' }}>
                <span style={{ 
                  fontWeight: 'bold', 
                  marginLeft: '8px'
                }}>
                  {field.label}:
                </span>
                <span style={{color: '#707070'}}>{field.value}</span>
              </div>
            ) : null
          ))}
                    {/* Keywords section (moved from collapsed view) */}
                    {tube.keywords && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                marginTop: '8px'
              }}>
                {tube.keywords.map((keyword, idx) => (
                  <span
                    key={idx}
                    style={{
                      backgroundColor: '#D9FEF9',
                      borderRadius: '9999px',
                      color: '#0C3058',
                      padding: '2px 10px',
                      marginLeft: '8px',
                      marginBottom: '4px',
                      fontSize: '16px',
                      fontWeight: 500,
                      border: '1px solid #D9FEF9'
                    }}
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default TestTubeRow;
